import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Alert,
  useTheme,
  Snackbar,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Save as SaveIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { format, addHours } from 'date-fns';

// Types
interface Host {
  id: number;
  name: string;
  department: string;
  email: string;
}

interface PreRegisterFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  purpose: string;
  host_id: number | '';
  expected_arrival: Date;
  expected_departure: Date;
  notes: string;
  send_invitation: boolean;
}

// Form validation
interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  purpose?: string;
  host_id?: string;
  expected_arrival?: string;
  expected_departure?: string;
}

const validateForm = (data: PreRegisterFormData): FormErrors => {
  const errors: FormErrors = {};
  
  if (!data.name.trim()) {
    errors.name = 'Name is required';
  }
  
  if (!data.email.trim()) {
    errors.email = 'Email is required for pre-registration';
  } else if (!/^\S+@\S+\.\S+$/.test(data.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (!data.phone.trim()) {
    errors.phone = 'Phone number is required';
  }
  
  if (!data.purpose.trim()) {
    errors.purpose = 'Purpose is required';
  }
  
  if (data.host_id === '') {
    errors.host_id = 'Please select a host';
  }
  
  if (data.expected_departure < data.expected_arrival) {
    errors.expected_departure = 'Departure time must be after arrival time';
  }
  
  return errors;
};

const PreRegister: React.FC = () => {
  // State for form data
  const [formData, setFormData] = useState<PreRegisterFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    purpose: '',
    host_id: '',
    expected_arrival: addHours(new Date(), 24), // Default to tomorrow
    expected_departure: addHours(new Date(), 25), // Default to 1 hour visit
    notes: '',
    send_invitation: true
  });
  
  // State for hosts
  const [hosts, setHosts] = useState<Host[]>([]);
  const [loadingHosts, setLoadingHosts] = useState(true);
  
  // State for form submission
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [registeredVisitorId, setRegisteredVisitorId] = useState<number | null>(null);
  const [checkInUrl, setCheckInUrl] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Purpose options
  const purposeOptions = [
    'Meeting',
    'Interview',
    'Delivery',
    'Presentation',
    'Client Meeting',
    'Service',
    'Training',
    'Conference',
    'Inspection',
    'Tour',
    'Other'
  ];
  
  // Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Fetch hosts from API
  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoadingHosts(true);
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await api.get(`/api/users/hosts?t=${timestamp}`);
        setHosts(response.data);
        setLoadingHosts(false);
      } catch (err) {
        console.error('Error fetching hosts:', err);
        setLoadingHosts(false);
      }
    };
    
    fetchHosts();
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name as string]: undefined
      }));
    }
  };
  
  // Handle date changes
  const handleArrivalChange = (value: unknown) => {
    const date = value as Date | null;
    if (date) {
      setFormData(prev => ({
        ...prev,
        expected_arrival: date,
        // Update departure time to be 1 hour after arrival if current departure is before new arrival
        expected_departure: prev.expected_departure < date ? addHours(date, 1) : prev.expected_departure
      }));
      
      // Clear error if it exists
      if (errors.expected_arrival) {
        setErrors(prev => ({
          ...prev,
          expected_arrival: undefined
        }));
      }
    }
  };
  
  const handleDepartureChange = (value: unknown) => {
    const date = value as Date | null;
    if (date) {
      setFormData(prev => ({
        ...prev,
        expected_departure: date
      }));
      
      // Clear error if it exists
      if (errors.expected_departure) {
        setErrors(prev => ({
          ...prev,
          expected_departure: undefined
        }));
      }
    }
  };
  
  // Handle invitation toggle
  const handleInvitationToggle = () => {
    setFormData(prev => ({
      ...prev,
      send_invitation: !prev.send_invitation
    }));
  };
  
  // Handle host change
  const handleHostChange = (event: SelectChangeEvent<number>) => {
    setFormData(prev => ({
      ...prev,
      host_id: event.target.value as number
    }));
    
    // Clear error if it exists
    if (errors.host_id) {
      setErrors(prev => ({
        ...prev,
        host_id: undefined
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Format dates for API
      const formattedData = {
        ...formData,
        expected_arrival: formData.expected_arrival.toISOString(),
        expected_departure: formData.expected_departure.toISOString()
      };
      
      const response = await api.post('/api/visitors/pre-register', formattedData);
      
      // Set success state
      setSubmitSuccess(true);
      setRegisteredVisitorId(response.data.id);
      
      // Generate check-in URL
      const baseUrl = window.location.origin;
      const checkInLink = `${baseUrl}/check-in?visitor=${response.data.id}`;
      setCheckInUrl(checkInLink);
      
      setIsSubmitting(false);
    } catch (err: any) {
      console.error('Error pre-registering visitor:', err);
      setSubmitError(err.response?.data?.message || 'Failed to pre-register visitor. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Handle copy to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(checkInUrl).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };
  
  // Handle reset form
  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      purpose: '',
      host_id: '',
      expected_arrival: addHours(new Date(), 24),
      expected_departure: addHours(new Date(), 25),
      notes: '',
      send_invitation: true
    });
    setErrors({});
    setSubmitSuccess(false);
    setRegisteredVisitorId(null);
    setCheckInUrl('');
  };
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Pre-Register Visitor
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pre-register an expected visitor to streamline their check-in process when they arrive.
        </Typography>
      </Box>
      
      {/* Success message */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
      {/* Form content */}
      <Paper sx={{ p: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        
        {submitSuccess ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Visitor pre-registration successful! {formData.send_invitation && 'An invitation has been sent to the visitor.'}
            </Alert>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pre-Registration Details
                </Typography>
                
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Visitor
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.email} • {formData.phone}
                    </Typography>
                    {formData.company && (
                      <Typography variant="body2" color="text.secondary">
                        {formData.company}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Visit Information
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      Expected: {format(formData.expected_arrival, 'PPP')}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      Time: {format(formData.expected_arrival, 'p')} - {format(formData.expected_departure, 'p')}
                    </Typography>
                    <Typography variant="body2">
                      Purpose: {formData.purpose}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Check-In Link
                </Typography>
                
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Share this link with the visitor or at the reception desk for quick check-in:
                </Typography>
                
                <TextField
                  fullWidth
                  value={checkInUrl}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={handleCopyLink}
                          title="Copy to clipboard"
                        >
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </CardContent>
            </Card>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => navigate('/visitors')}
              >
                Back to Visitors
              </Button>
              
              <Button
                variant="contained"
                onClick={handleReset}
              >
                Register Another Visitor
              </Button>
            </Box>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Visitor Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Visitor Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!!errors.purpose}
                  helperText={errors.purpose}
                >
                  {purposeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.host_id} required>
                  <InputLabel id="host-select-label">Host</InputLabel>
                  <Select
                    labelId="host-select-label"
                    name="host_id"
                    value={formData.host_id}
                    onChange={handleHostChange}
                    label="Host"
                  >
                    {loadingHosts ? (
                      <MenuItem disabled>Loading hosts...</MenuItem>
                    ) : (
                      hosts.map((host) => (
                        <MenuItem key={host.id} value={host.id}>
                          {host.name} ({host.department})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.host_id && <FormHelperText>{errors.host_id}</FormHelperText>}
                </FormControl>
              </Grid>
              
              {/* Visit Schedule */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Visit Schedule
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Expected Arrival"
                    value={formData.expected_arrival}
                    onChange={handleArrivalChange}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        fullWidth
                        error={!!errors.expected_arrival}
                        helperText={errors.expected_arrival || props.helperText}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Expected Departure"
                    value={formData.expected_departure}
                    onChange={handleDepartureChange}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        fullWidth
                        error={!!errors.expected_departure}
                        helperText={errors.expected_departure || props.helperText}
                      />
                    )}
                  />
                </Grid>
              </LocalizationProvider>
              
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional information about the visit"
                />
              </Grid>
              
              {/* Notification Settings */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Button
                    variant={formData.send_invitation ? "contained" : "outlined"}
                    startIcon={<SendIcon />}
                    onClick={handleInvitationToggle}
                    sx={{ mb: 1 }}
                  >
                    {formData.send_invitation ? "Send Invitation Email" : "Don't Send Invitation"}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {formData.send_invitation 
                      ? "An email with visit details and check-in instructions will be sent to the visitor."
                      : "No email notification will be sent to the visitor."}
                  </Typography>
                </FormControl>
              </Grid>
              
              {/* Form Actions */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/visitors')}
                  >
                    Cancel
                  </Button>
                  
                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      disabled={isSubmitting}
                    >
                      Pre-Register Visitor
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default PreRegister;
